/**
 * IFS Shipping Hub - Shared styled components for tab content
 */
import styled, { keyframes } from 'styled-components';
const fadeIn = keyframes `
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;
const spin = keyframes `
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
// ─── Layout ──────────────────────────────────────────────
export const ToolSection = styled.div `
  margin-bottom: 32px;
  animation: ${fadeIn} 0.3s ease;
`;
export const SectionTitle = styled.h3 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-color);

  span {
    color: var(--accent-gold, #B8860B);
  }
`;
export const SectionDescription = styled.p `
  color: var(--text-secondary);
  font-size: 13px;
  margin: 0 0 16px 0;
  line-height: 1.5;
`;
// ─── Forms ───────────────────────────────────────────────
export const FormGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(${p => p.$columns || 3}, 1fr);
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;
export const FormField = styled.div `
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
export const FieldLabel = styled.label `
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
export const FieldInput = styled.input `
  padding: 10px 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #B8860B);
    box-shadow: 0 0 0 3px var(--accent-gold-bg, rgba(184, 134, 11, 0.08));
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;
export const FieldSelect = styled.select `
  padding: 10px 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #B8860B);
    box-shadow: 0 0 0 3px var(--accent-gold-bg, rgba(184, 134, 11, 0.08));
  }
`;
export const ActionButton = styled.button `
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${p => p.$variant === 'secondary' ? 'var(--border-color)' : 'var(--accent-gold, #B8860B)'};
  background: ${p => p.$variant === 'secondary' ? 'var(--bg-tertiary)' : 'var(--accent-gold, #B8860B)'};
  color: ${p => p.$variant === 'secondary' ? 'var(--text-primary)' : '#fff'};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
// ─── Results ─────────────────────────────────────────────
export const ResultPanel = styled.div `
  background: var(--bg-primary);
  border: 1px solid ${p => p.$status === 'success' ? 'var(--success-color, #00c853)' :
    p.$status === 'error' ? 'var(--error-color, #ff1744)' :
        p.$status === 'warning' ? 'var(--warning-color, #ffa500)' :
            'var(--border-color)'};
  padding: 20px;
  margin-top: 16px;
  animation: ${fadeIn} 0.3s ease;
`;
export const ResultHeader = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;
export const ResultTitle = styled.h4 `
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;
export const ResponseTime = styled.span `
  font-size: 12px;
  font-family: 'JetBrains Mono', monospace;
  color: var(--text-tertiary);
  background: var(--bg-tertiary);
  padding: 2px 8px;
`;
// ─── Tables ──────────────────────────────────────────────
export const DataTable = styled.table `
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    padding: 10px 12px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-tertiary);
    background: var(--bg-tertiary);
    border-bottom: 2px solid var(--accent-gold, #B8860B);
    position: sticky;
    top: 0;
    z-index: 1;
  }

  td {
    padding: 8px 12px;
    color: var(--text-primary);
    border-bottom: 1px solid var(--border-color);
  }

  tr:hover td {
    background: var(--bg-tertiary);
  }
`;
export const TableWrapper = styled.div `
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid var(--border-color);
`;
// ─── JSON Viewer ─────────────────────────────────────────
export const JsonViewer = styled.pre `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 16px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: var(--text-secondary);
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-all;
`;
// ─── Cards / Info ────────────────────────────────────────
export const InfoCard = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 16px;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent-gold-border, rgba(184, 134, 11, 0.25));
  }
`;
export const InfoCardGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
`;
export const InfoRow = styled.div `
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid var(--border-color);
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }
`;
export const InfoLabel = styled.span `
  color: var(--text-tertiary);
  font-size: 12px;
`;
export const InfoValue = styled.span `
  color: var(--text-primary);
  font-weight: 500;
  text-align: right;
`;
// ─── Status / Badges ─────────────────────────────────────
export const StatusBadge = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${p => p.$status === 'success' ? 'rgba(0, 200, 83, 0.1)' :
    p.$status === 'error' ? 'rgba(255, 23, 68, 0.1)' :
        p.$status === 'warning' ? 'rgba(255, 165, 0, 0.1)' :
            'rgba(184, 134, 11, 0.1)'};
  color: ${p => p.$status === 'success' ? '#00c853' :
    p.$status === 'error' ? '#ff1744' :
        p.$status === 'warning' ? '#ffa500' :
            'var(--accent-gold, #B8860B)'};
  border: 1px solid currentColor;
`;
// ─── Loading ─────────────────────────────────────────────
export const LoadingSpinner = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--text-tertiary);
  font-size: 14px;
  gap: 10px;

  &::before {
    content: '';
    width: 20px;
    height: 20px;
    border: 2px solid var(--border-color);
    border-top-color: var(--accent-gold, #B8860B);
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
  }
`;
export const EmptyState = styled.div `
  text-align: center;
  padding: 40px;
  color: var(--text-tertiary);
  font-size: 14px;
`;
// ─── Search Bar ──────────────────────────────────────────
export const SearchBar = styled.div `
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  align-items: flex-end;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;
// ─── Tab internal sub-sections ───────────────────────────
export const SubSection = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 20px;
  margin-bottom: 20px;
`;
export const SubSectionHeader = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  cursor: pointer;

  h4 {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;
// ─── Two-column detail view ──────────────────────────────
export const DetailGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
export const DetailColumn = styled.div ``;
export const DetailTitle = styled.h4 `
  font-size: 13px;
  font-weight: 600;
  color: var(--accent-gold, #B8860B);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
`;
// ─── Help Tooltip ────────────────────────────────────────
export const HelpTooltipWrapper = styled.span `
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  vertical-align: middle;
`;
export const HelpIcon = styled.span `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid var(--text-tertiary);
  color: var(--text-tertiary);
  font-size: 11px;
  font-weight: 700;
  font-family: 'JetBrains Mono', monospace;
  cursor: help;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    border-color: var(--accent-gold, #B8860B);
    color: var(--accent-gold, #B8860B);
    background: var(--accent-gold-bg, rgba(184, 134, 11, 0.08));
  }
`;
const tooltipFade = keyframes `
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;
export const HelpBubble = styled.div `
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  width: 320px;
  padding: 14px 16px;
  background: var(--bg-primary);
  border: 1px solid var(--accent-gold, #B8860B);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  z-index: 99999;
  animation: ${tooltipFade} 0.15s ease;
  pointer-events: auto;

  /* Arrow */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: var(--accent-gold, #B8860B);
  }

  @media (max-width: 600px) {
    width: 260px;
    left: 0;
    transform: none;

    &::after {
      left: 16px;
      transform: none;
    }
  }
`;
export const HelpBubbleTitle = styled.div `
  font-size: 12px;
  font-weight: 700;
  color: var(--accent-gold, #B8860B);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;
export const HelpBubbleText = styled.div `
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.6;
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
`;
export const HelpBubbleExample = styled.div `
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border-color);
  font-size: 12px;
  color: var(--text-tertiary);
  line-height: 1.6;
  font-style: normal;
  font-family: 'Poppins', sans-serif;

  strong {
    color: var(--text-secondary);
    font-style: normal;
  }
`;
